/**
 * Meeting Templates Page
 * Create, manage, and use meeting templates for quick meeting creation
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, Plus, Edit, Trash2, Copy, Users, Clock, MapPin,
  Video, Building2, Calendar, Loader2, Search, MoreHorizontal,
  PlayCircle
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL;

const MeetingTemplates = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [useTemplateDialogOpen, setUseTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    meeting_type: 'physical',
    default_duration_minutes: 60,
    default_attendee_ids: [],
    default_external_attendees: [],
    default_agenda: '',
    default_location: ''
  });
  
  // Use template form state
  const [useFormData, setUseFormData] = useState({
    title: '',
    scheduled_start: '',
    scheduled_end: ''
  });
  
  const [externalAttendeeInput, setExternalAttendeeInput] = useState({ name: '', email: '' });

  useEffect(() => {
    fetchTemplates();
    fetchOrgUsers();
  }, [token]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/meeting-intelligence/templates?token=${token}`);
      setTemplates(res.data.templates || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgUsers = async () => {
    try {
      const res = await axios.get(`${API}/api/meeting-intelligence/users?token=${token}`);
      setOrgUsers(res.data.users || []);
    } catch (err) {
      console.error('Error fetching org users:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      meeting_type: 'physical',
      default_duration_minutes: 60,
      default_attendee_ids: [],
      default_external_attendees: [],
      default_agenda: '',
      default_location: ''
    });
    setExternalAttendeeInput({ name: '', email: '' });
  };

  const handleCreateTemplate = async () => {
    if (!formData.name.trim()) {
      toast.error('Template name is required');
      return;
    }
    
    try {
      setSaving(true);
      await axios.post(`${API}/api/meeting-intelligence/templates?token=${token}`, formData);
      toast.success('Template created successfully');
      setCreateDialogOpen(false);
      resetForm();
      fetchTemplates();
    } catch (err) {
      console.error('Error creating template:', err);
      toast.error(err.response?.data?.detail || 'Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTemplate = async () => {
    if (!formData.name.trim()) {
      toast.error('Template name is required');
      return;
    }
    
    try {
      setSaving(true);
      await axios.put(
        `${API}/api/meeting-intelligence/templates/${selectedTemplate.id}?token=${token}`,
        formData
      );
      toast.success('Template updated successfully');
      setEditDialogOpen(false);
      resetForm();
      fetchTemplates();
    } catch (err) {
      console.error('Error updating template:', err);
      toast.error(err.response?.data?.detail || 'Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (!confirm(`Delete template "${template.name}"? This cannot be undone.`)) return;
    
    try {
      await axios.delete(`${API}/api/meeting-intelligence/templates/${template.id}?token=${token}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      toast.error('Failed to delete template');
    }
  };

  const handleUseTemplate = async () => {
    if (!useFormData.title.trim() || !useFormData.scheduled_start) {
      toast.error('Title and start time are required');
      return;
    }
    
    try {
      setSaving(true);
      const res = await axios.post(
        `${API}/api/meeting-intelligence/templates/${selectedTemplate.id}/use?token=${token}`,
        {
          title: useFormData.title,
          scheduled_start: new Date(useFormData.scheduled_start).toISOString(),
          scheduled_end: useFormData.scheduled_end ? new Date(useFormData.scheduled_end).toISOString() : null
        }
      );
      
      toast.success('Meeting created from template');
      setUseTemplateDialogOpen(false);
      navigate(`/meeting-intelligence/meetings/${res.data.meeting.id}`);
    } catch (err) {
      console.error('Error using template:', err);
      toast.error(err.response?.data?.detail || 'Failed to create meeting');
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      meeting_type: template.meeting_type || 'physical',
      default_duration_minutes: template.default_duration_minutes || 60,
      default_attendee_ids: template.default_attendee_ids || [],
      default_external_attendees: template.default_external_attendees || [],
      default_agenda: template.default_agenda || '',
      default_location: template.default_location || ''
    });
    setEditDialogOpen(true);
  };

  const openUseDialog = (template) => {
    setSelectedTemplate(template);
    setUseFormData({
      title: '',
      scheduled_start: '',
      scheduled_end: ''
    });
    setUseTemplateDialogOpen(true);
  };

  const addInternalAttendee = (user) => {
    if (!formData.default_attendee_ids.includes(user.id)) {
      setFormData(prev => ({
        ...prev,
        default_attendee_ids: [...prev.default_attendee_ids, user.id]
      }));
    }
  };

  const removeInternalAttendee = (userId) => {
    setFormData(prev => ({
      ...prev,
      default_attendee_ids: prev.default_attendee_ids.filter(id => id !== userId)
    }));
  };

  const addExternalAttendee = () => {
    if (externalAttendeeInput.name && externalAttendeeInput.email) {
      setFormData(prev => ({
        ...prev,
        default_external_attendees: [
          ...prev.default_external_attendees,
          { name: externalAttendeeInput.name, email: externalAttendeeInput.email }
        ]
      }));
      setExternalAttendeeInput({ name: '', email: '' });
    }
  };

  const removeExternalAttendee = (index) => {
    setFormData(prev => ({
      ...prev,
      default_external_attendees: prev.default_external_attendees.filter((_, i) => i !== index)
    }));
  };

  const getMeetingTypeIcon = (type) => {
    switch (type) {
      case 'teams': return <Video className="h-4 w-4 text-purple-600" />;
      case 'hybrid': return <Users className="h-4 w-4 text-amber-600" />;
      default: return <Building2 className="h-4 w-4 text-blue-600" />;
    }
  };

  const filteredTemplates = templates.filter(t =>
    !searchQuery ||
    t.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="meeting-templates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Meeting Templates</h1>
          <p className="text-slate-500">Save frequently used meeting configurations for quick reuse</p>
        </div>
        <Button onClick={() => { resetForm(); setCreateDialogOpen(true); }} data-testid="create-template-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Template
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search templates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="template-search"
        />
      </div>

      {/* Templates Grid */}
      {filteredTemplates.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <Card key={template.id} className="hover:shadow-md transition-shadow" data-testid={`template-${template.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                    <CardTitle className="text-lg">{template.name}</CardTitle>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openUseDialog(template)}>
                        <PlayCircle className="h-4 w-4 mr-2" /> Use Template
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEditDialog(template)}>
                        <Edit className="h-4 w-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        className="text-red-600"
                        onClick={() => handleDeleteTemplate(template)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                {template.description && (
                  <CardDescription className="line-clamp-2">{template.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="flex items-center gap-1">
                    {getMeetingTypeIcon(template.meeting_type)}
                    {template.meeting_type}
                  </Badge>
                  <Badge variant="outline" className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {template.default_duration_minutes} min
                  </Badge>
                  {template.default_location && (
                    <Badge variant="outline" className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {template.default_location}
                    </Badge>
                  )}
                </div>
                
                {template.attendees_info?.length > 0 && (
                  <div className="flex items-center gap-1 text-sm text-slate-500">
                    <Users className="h-4 w-4" />
                    <span>{template.attendees_info.length} default attendee(s)</span>
                  </div>
                )}
                
                <div className="flex items-center justify-between pt-2 border-t">
                  <span className="text-xs text-slate-400">
                    Used {template.usage_count || 0} times
                  </span>
                  <Button 
                    size="sm" 
                    onClick={() => openUseDialog(template)}
                    data-testid={`use-template-${template.id}`}
                  >
                    <PlayCircle className="h-4 w-4 mr-1" />
                    Use
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-16 w-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium mb-2">No Templates Yet</h3>
            <p className="text-slate-500 text-center mb-4">
              Create meeting templates to save time when scheduling similar meetings
            </p>
            <Button onClick={() => { resetForm(); setCreateDialogOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Template
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Template Dialog */}
      <Dialog open={createDialogOpen || editDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setCreateDialogOpen(false);
          setEditDialogOpen(false);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editDialogOpen ? 'Edit Template' : 'Create Meeting Template'}
            </DialogTitle>
            <DialogDescription>
              Save meeting configurations for quick reuse
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Template Name *</Label>
                <Input
                  placeholder="e.g., Weekly Team Standup"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="template-name-input"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Brief description of this template"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                />
              </div>
              
              <div>
                <Label>Meeting Type</Label>
                <Select 
                  value={formData.meeting_type} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, meeting_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="physical">Physical</SelectItem>
                    <SelectItem value="teams">Teams/Virtual</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Default Duration (minutes)</Label>
                <Input
                  type="number"
                  min={15}
                  max={480}
                  value={formData.default_duration_minutes}
                  onChange={(e) => setFormData(prev => ({ ...prev, default_duration_minutes: parseInt(e.target.value) || 60 }))}
                />
              </div>
              
              <div className="col-span-2">
                <Label>Default Location</Label>
                <Input
                  placeholder="e.g., Conference Room A"
                  value={formData.default_location}
                  onChange={(e) => setFormData(prev => ({ ...prev, default_location: e.target.value }))}
                />
              </div>
              
              <div className="col-span-2">
                <Label>Default Agenda</Label>
                <Textarea
                  placeholder="Meeting agenda template..."
                  value={formData.default_agenda}
                  onChange={(e) => setFormData(prev => ({ ...prev, default_agenda: e.target.value }))}
                  rows={3}
                />
              </div>
            </div>
            
            {/* Attendees Section */}
            <div className="border-t pt-4">
              <Label className="mb-3 block">Default Attendees</Label>
              
              {/* Selected Attendees */}
              {(formData.default_attendee_ids.length > 0 || formData.default_external_attendees.length > 0) && (
                <div className="flex flex-wrap gap-2 mb-3 p-3 bg-slate-50 rounded-lg">
                  {formData.default_attendee_ids.map(id => {
                    const user = orgUsers.find(u => u.id === id);
                    return user ? (
                      <Badge key={id} variant="secondary" className="flex items-center gap-1">
                        {user.full_name}
                        <button onClick={() => removeInternalAttendee(id)} className="ml-1 hover:text-red-500">×</button>
                      </Badge>
                    ) : null;
                  })}
                  {formData.default_external_attendees.map((ext, idx) => (
                    <Badge key={idx} variant="outline" className="flex items-center gap-1 bg-amber-50">
                      {ext.name} (External)
                      <button onClick={() => removeExternalAttendee(idx)} className="ml-1 hover:text-red-500">×</button>
                    </Badge>
                  ))}
                </div>
              )}
              
              {/* Add Internal Attendee */}
              <div className="mb-3">
                <p className="text-sm text-slate-500 mb-2">Add from organization:</p>
                <div className="grid grid-cols-3 gap-2 max-h-32 overflow-y-auto">
                  {orgUsers.map(user => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => addInternalAttendee(user)}
                      disabled={formData.default_attendee_ids.includes(user.id)}
                      className={`p-2 text-left text-sm rounded border transition-colors ${
                        formData.default_attendee_ids.includes(user.id)
                          ? 'bg-slate-100 text-slate-400'
                          : 'hover:bg-blue-50 hover:border-blue-200'
                      }`}
                    >
                      {user.full_name}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Add External Attendee */}
              <div className="border-t pt-3">
                <p className="text-sm text-slate-500 mb-2">Add external attendee:</p>
                <div className="flex gap-2">
                  <Input
                    placeholder="Name"
                    value={externalAttendeeInput.name}
                    onChange={(e) => setExternalAttendeeInput(prev => ({ ...prev, name: e.target.value }))}
                    className="flex-1"
                  />
                  <Input
                    placeholder="Email"
                    type="email"
                    value={externalAttendeeInput.email}
                    onChange={(e) => setExternalAttendeeInput(prev => ({ ...prev, email: e.target.value }))}
                    className="flex-1"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={addExternalAttendee}
                    disabled={!externalAttendeeInput.name || !externalAttendeeInput.email}
                  >
                    Add
                  </Button>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialogOpen(false); setEditDialogOpen(false); }}>
              Cancel
            </Button>
            <Button 
              onClick={editDialogOpen ? handleUpdateTemplate : handleCreateTemplate}
              disabled={saving || !formData.name.trim()}
              data-testid="save-template-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : editDialogOpen ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Use Template Dialog */}
      <Dialog open={useTemplateDialogOpen} onOpenChange={setUseTemplateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Meeting from Template</DialogTitle>
            <DialogDescription>
              Using template: {selectedTemplate?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Meeting Title *</Label>
              <Input
                placeholder="Enter meeting title"
                value={useFormData.title}
                onChange={(e) => setUseFormData(prev => ({ ...prev, title: e.target.value }))}
                data-testid="meeting-title-input"
              />
            </div>
            
            <div>
              <Label>Start Time *</Label>
              <Input
                type="datetime-local"
                value={useFormData.scheduled_start}
                onChange={(e) => setUseFormData(prev => ({ ...prev, scheduled_start: e.target.value }))}
                data-testid="meeting-start-input"
              />
            </div>
            
            <div>
              <Label>End Time (Optional)</Label>
              <Input
                type="datetime-local"
                value={useFormData.scheduled_end}
                onChange={(e) => setUseFormData(prev => ({ ...prev, scheduled_end: e.target.value }))}
              />
              <p className="text-xs text-slate-500 mt-1">
                Leave empty to use default duration ({selectedTemplate?.default_duration_minutes} minutes)
              </p>
            </div>
            
            {selectedTemplate?.attendees_info?.length > 0 && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium mb-2">Default Attendees:</p>
                <div className="flex flex-wrap gap-1">
                  {selectedTemplate.attendees_info.map((att, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      {att.name} {!att.is_internal && '(External)'}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setUseTemplateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUseTemplate}
              disabled={saving || !useFormData.title.trim() || !useFormData.scheduled_start}
              data-testid="create-meeting-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Calendar className="h-4 w-4 mr-2" />
                  Create Meeting
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MeetingTemplates;
